import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { JobCard, JobFilters } from '../../components/jobs';
import { searchJobs, saveJob, unsaveJob, getSavedJobs } from '../../services/jobs';
import { useAuth } from '../../context/AuthContext';
import type { JobSearchParams, JobSearchResult, SavedJob } from '../../types/job';

export function JobBoardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, userType } = useAuth();

  const [filters, setFilters] = useState<JobSearchParams>(() => {
    // Initialize from URL params
    const params: JobSearchParams = {
      page: parseInt(searchParams.get('page') || '1'),
      limit: parseInt(searchParams.get('limit') || '20'),
    };

    if (searchParams.get('q')) params.q = searchParams.get('q')!;
    if (searchParams.get('category')) params.category = searchParams.get('category')!;
    if (searchParams.get('city')) params.city = searchParams.get('city')!;
    if (searchParams.get('sort')) params.sort = searchParams.get('sort') as any;

    const jobTypes = searchParams.getAll('job_type');
    if (jobTypes.length) params.job_type = jobTypes as any;

    const expLevels = searchParams.getAll('experience_level');
    if (expLevels.length) params.experience_level = expLevels as any;

    const locTypes = searchParams.getAll('location_type');
    if (locTypes.length) params.location_type = locTypes as any;

    return params;
  });

  const [result, setResult] = useState<JobSearchResult | null>(null);
  const [savedJobIds, setSavedJobIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sync filters to URL
  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.q) params.set('q', filters.q);
    if (filters.category) params.set('category', filters.category);
    if (filters.city) params.set('city', filters.city);
    if (filters.sort) params.set('sort', filters.sort);
    if (filters.page && filters.page > 1) params.set('page', filters.page.toString());

    filters.job_type?.forEach(t => params.append('job_type', t));
    filters.experience_level?.forEach(l => params.append('experience_level', l));
    filters.location_type?.forEach(l => params.append('location_type', l));

    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // Load saved jobs for authenticated experts
  useEffect(() => {
    if (isAuthenticated && userType === 'expert') {
      getSavedJobs()
        .then((saved: SavedJob[]) => {
          setSavedJobIds(new Set(saved.map(s => s.job_id)));
        })
        .catch(console.error);
    }
  }, [isAuthenticated, userType]);

  // Search jobs
  const doSearch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await searchJobs(filters);
      setResult(data);
    } catch (err) {
      console.error('Failed to search jobs:', err);
      setError('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Initial load and filter changes
  useEffect(() => {
    doSearch();
  }, [doSearch]);

  const handleSaveToggle = async (jobId: string) => {
    if (!isAuthenticated) {
      // Prompt to login
      return;
    }

    try {
      if (savedJobIds.has(jobId)) {
        await unsaveJob(jobId);
        setSavedJobIds(prev => {
          const next = new Set(prev);
          next.delete(jobId);
          return next;
        });
      } else {
        await saveJob(jobId);
        setSavedJobIds(prev => new Set(prev).add(jobId));
      }
    } catch (err) {
      console.error('Failed to save/unsave job:', err);
    }
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4">Find Your Next IT Role</h1>
          <p className="text-xl text-blue-100">
            Browse verified opportunities from Ireland's leading tech companies
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="mb-8">
          <JobFilters filters={filters} onChange={setFilters} onSearch={doSearch} />
        </div>

        {/* Results header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            {result && (
              <p className="text-gray-600">
                Showing{' '}
                <span className="font-medium">
                  {(result.page - 1) * result.page_size + 1}-
                  {Math.min(result.page * result.page_size, result.total)}
                </span>{' '}
                of <span className="font-medium">{result.total}</span> jobs
              </p>
            )}
          </div>
        </div>

        {/* Error state */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <p className="text-red-700">{error}</p>
            <button onClick={doSearch} className="mt-2 text-red-600 underline text-sm">
              Try again
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="grid gap-4 md:grid-cols-2">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg" />
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
                <div className="flex gap-2 mb-4">
                  <div className="h-6 bg-gray-200 rounded w-20" />
                  <div className="h-6 bg-gray-200 rounded w-24" />
                  <div className="h-6 bg-gray-200 rounded w-16" />
                </div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        )}

        {/* Results */}
        {!loading && result && (
          <>
            {(!result.jobs || result.jobs.length === 0) ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <svg
                  className="w-16 h-16 text-gray-400 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
                <p className="text-gray-600">
                  Try adjusting your search filters or check back later for new opportunities.
                </p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {result.jobs.map(job => (
                  <JobCard
                    key={job.id}
                    job={job}
                    onSave={isAuthenticated && userType === 'expert' ? handleSaveToggle : undefined}
                    isSaved={savedJobIds.has(job.id)}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {result.total_pages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  onClick={() => handlePageChange(result.page - 1)}
                  disabled={result.page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                {[...Array(result.total_pages)].map((_, i) => {
                  const page = i + 1;
                  // Show first, last, current, and neighbors
                  if (
                    page === 1 ||
                    page === result.total_pages ||
                    Math.abs(page - result.page) <= 1
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 border rounded-md text-sm font-medium ${
                          page === result.page
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === 2 ||
                    page === result.total_pages - 1
                  ) {
                    return (
                      <span key={page} className="px-2 text-gray-400">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}

                <button
                  onClick={() => handlePageChange(result.page + 1)}
                  disabled={result.page === result.total_pages}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
